import api from "./client";

// Note: markCommunicationResponded, previewChaser and sendChaser were removed
// on 2026-08-27. They pointed at /communications/{id}/response,
// /chaser-preview and /chaser-send, none of which exist in the API, and no
// component called them.

export const getCommunications = (params) => api.get("/communications", { params });

// Display-only English translation of up to 40 comms (ISS-8cc5c6e0). Cached
// server side; the original message is never changed.
export const translateCommunications = (ids) =>
  api.get("/communications/translate", { params: { ids: ids.join(",") } });

// Reply boxes on the enquiry Communications tabs (ISS-fd8dd14b).
// English draft -> recipient's language with the English below. Sends nothing.
export const translateReplyDraft = (data) => api.post("/communications/reply-translate", data);
// Reply to one supplier email in its own Gmail thread (same endpoint as the Suppliers tab).
export const sendSupplierThreadReply = (data) => api.post("/outreach/send-custom", data);

// Everything the client sees about an enquiry on the Client Comms tab:
// their emails, our replies, chasers, the booking confirmation and invoices (Fan, 2026-10-02).
export const CLIENT_COMM_TYPES = [
  "client_enquiry", "client_update", "client", "email", "client_chaser", "quote_chaser",
  "guest_details_chaser", "guest_id_chaser", "client_confirmation", "guest", "checkin_chaser",
  "invoice", "invoice_due_batch", "updated_invoice", "payment_reminder", "payment_confirmation",
  "missed_call_autoreply", "client_call",
  // Outgoing from our side (Fan, 2026-10-06): our Gmail replies, invoice and
  // check-in emails, stay notices, info requests.
  "client_sent_email", "invoice_email", "guest_email_auto", "guest_whatsapp_auto",
  "stay_end_notice", "info_request", "client_task_agent_reply", "presentation",
  "quote_sent_externally", "rolling_invoice", "viewing", "viewing_session",
].join(",");

// Paged read of the whole matching set. The Inbox used to ask for a single
// newest-500 page, so on a busy week older threads silently fell out of the
// list (ENQ-00372 / Casa Calmar, 2026-09-20). Pages until a short page comes
// back, with a hard ceiling so a runaway filter cannot hang the page.
export const getAllCommunications = async (params, { pageSize = 500, maxPages = 12 } = {}) => {
  const rows = [];
  for (let page = 0; page < maxPages; page++) {
    const res = await api.get("/communications", {
      params: { ...params, limit: pageSize, offset: page * pageSize },
    });
    const batch = res.data || [];
    rows.push(...batch);
    // Stop on an empty page, never on a short one: the API silently skips
    // rows that fail response validation, so a full page can come back
    // short while older pages still hold real messages.
    if (batch.length === 0) break;
  }
  return { data: rows };
};
export const createCommunication = (data) => api.post("/communications", data);
export const previewChaserByEnquiry = (enquiryId) =>
  api.get(`/communications/chaser/${enquiryId}/preview`);
export const sendChaserByEnquiry = (enquiryId, data) =>
  api.post(`/communications/chaser/${enquiryId}/send`, data);
// Unread per Inbox tab + pending chasers, same predicates as the tab lists.
// Tabs, header and sidebar all show these numbers (Fan, 2026-10-04).
export const getInboxCounts = () => api.get("/communications/inbox-counts");
export const getPendingChasers = (params) => api.get("/communications/pending-chasers", { params });
export const dismissChaser = (chaserId) => api.post(`/communications/pending-chasers/${chaserId}/dismiss`);
export const markPendingChaserDone = (chaserId) => api.post(`/communications/pending-chasers/${chaserId}/mark-done`);
export const markInvoicesAsChased = (invoiceIds) => api.post("/communications/pending-chasers/mark-chased", { invoice_ids: invoiceIds });
export const syncInbox = () => api.post("/communications/sync-inbox");
export const syncFinanceInbox = () => api.post("/communications/sync-finance");
export const getWisePayments = () => api.get("/communications/wise-payments");
export const retryWisePaymentEmails = (paymentId) => api.post(`/communications/wise-payments/${paymentId}/retry-emails`);
export const resyncWisePayment = (paymentId) => api.post(`/communications/wise-payments/${paymentId}/resync`);
export const deleteWisePayment = (paymentId) => api.delete(`/communications/wise-payments/${paymentId}`);
export const getWisePaymentsPendingConfirmations = () => api.get("/communications/wise-payments/pending-confirmations");
export const previewWisePaymentConfirmation = (paymentId) => api.get(`/communications/wise-payments/${paymentId}/confirmation-preview`);
export const sendWisePaymentConfirmation = (paymentId, data) => api.post(`/communications/wise-payments/${paymentId}/send-confirmation`, data);
export const getWiseOutgoingPayments = () => api.get("/communications/wise-outgoing-payments");
export const getWiseOutgoingPaymentsPendingRemittances = () => api.get("/communications/wise-outgoing-payments/pending-remittances");
export const previewRemittanceAdvice = (paymentId) => api.get(`/communications/wise-outgoing-payments/${paymentId}/remittance-preview`);
export const sendRemittanceAdvice = (paymentId, data) => api.post(`/communications/wise-outgoing-payments/${paymentId}/send-remittance`, data);
export const getSyncStatus = () => api.get("/communications/sync-status");
// communicationIds: mark exactly these rows (the open conversation's unread
// messages). Needed for threads with no supplier_id, guest and call rows.
export const markReadBulk = (supplierId, enquiryId, commType, communicationIds) =>
  api.post(
    "/communications/mark-read-bulk",
    communicationIds?.length ? { communication_ids: communicationIds } : null,
    { params: { supplier_id: supplierId || undefined, enquiry_id: enquiryId || undefined, comm_type: commType || undefined } },
  );
export const linkCommunicationToSupplier = (communicationId, supplierId) =>
  api.post(`/communications/${communicationId}/link-supplier`, null, { params: { supplier_id: supplierId } });
export const createSupplierFromComm = (communicationId, name, country) =>
  api.post("/communications/create-supplier-from-comm", null, { params: { communication_id: communicationId, name, country } });
export const getStyleProfile = () => api.get("/communications/style-profile");
export const rebuildStyleProfile = (channel) => api.post("/communications/style-profile/rebuild", null, { params: { channel } });
export const reprocessMessage = (communicationId, overrides = {}) => api.post(`/communications/${communicationId}/reprocess`, overrides);
export const createDraftOptionFromCommunication = (communicationId, overrides = {}) => api.post(`/communications/${communicationId}/create-draft-option`, overrides);
export const linkCommunicationToEnquiry = (communicationId, enquiryId) =>
  api.post(`/communications/${communicationId}/link-enquiry`, null, { params: { enquiry_id: enquiryId } });
// Pick the stay a flagged supplier email is about (links its Gmail thread only).
export const resolveCommunicationLink = (communicationId, enquiryId, bookingId = null) =>
  api.post(`/communications/${communicationId}/resolve-link`, { enquiry_id: enquiryId, booking_id: bookingId });
export const unlinkCommunicationFromEnquiry = (communicationId, enquiryId) =>
  api.post(`/communications/${communicationId}/unlink-enquiry`, null, { params: { enquiry_id: enquiryId } });
export const getEnquiryThread = (enquiryId) => api.get(`/communications/thread/${enquiryId}`);
export const getSentCommunications = (params) => api.get("/communications/sent", { params });
export const associateReconciliation = (reconId, gmailMessageId) =>
  api.post(`/communications/reconciliations/${reconId}/associate`, { gmail_message_id: gmailMessageId });
export const reassignCommunication = (commId, data) =>
  api.post(`/communications/${commId}/reassign`, data);
